import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
} from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";

import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import FormSearchSelect from "../common/FormSearchSelect";
import FormTextField from "../common/FormTextField";
import StandardTable, { TableColumn } from "../common/StandardTable";
import ConfirmDialog from "../common/ConfirmDialog";
import useWorkspaceNavigation from "../../hooks/useWorkspaceNavigation";
import AppButton from "../buttons/AppButton";

import useDebounce from "../../hooks/useDebounce";
import { formatCurrency } from "../../utils/currencyUtils";
import { formatPlainNumber } from "../../utils/appUtils";
import FormDateField from "../common/FormDateField";
import FormAlert from "../common/FormAlert";

import { getItems } from "../../redux/actions/itemActions";
import { getCustomers } from "../../redux/actions/customerActions";
import { getCurrencies } from "../../redux/actions/currencyActions";
import { getCompanyCharges } from "../../redux/actions/companyChargeActions";
import { getBranchSettings } from "../../redux/actions/settingsActions";
import {
  checkCashierHasOpenShift,
  getCurrentOrRecentShift,
} from "../../redux/actions/shiftActions";
import {
  addInvItem,
  updateInvItemFields,
  removeInvItem,
  clearInvCart,
  changeInvoiceSaleType,
  createInvoice,
  resetInvoiceServerStatus,
} from "../../redux/actions/invoiceActions";
import { useAppDispatch, useAppSelector } from "../../redux/store";

import { InvoiceRequestDto } from "../../shared/dtos/invoice/InvoiceRequestDto";
import { SaleType } from "../../shared/enums/SaleType";
import { ItemResponseDto } from "../../shared/dtos/item/ItemResponseDto";

interface InvoiceFormValues {
  customerId: string;
  dueDate: string;
  currencyCode: string;
  exchangeRate: number | "";
  chargeIds: string[];
}

interface CreateInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  error: string | null;
}

const validationSchema = Yup.object().shape({
  customerId: Yup.string().required("Customer is required"),
  dueDate: Yup.string().required("Due date is required"),
  currencyCode: Yup.string().required("Currency is required"),
  exchangeRate: Yup.number().required("Exchange rate is required").min(0.01),
});

const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
  open,
  onClose,
  error,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { navigateToTab } = useWorkspaceNavigation();

  // --- REDUX SUBSCRIPTIONS ---
  const { hasOpenShift, currentShift, loadingHasOpenShift } = useAppSelector(
    (state) => state.shift,
  );
  const {
    items,
    pagination,
    loadingFetchAll: loadingItems,
  } = useAppSelector((state) => state.item);
  const { items: customers, loadingFetchAll: loadingCustomers } =
    useAppSelector((state) => state.customer);
  const { items: currencies } = useAppSelector((state) => state.currency);
  const { items: companyCharges } = useAppSelector(
    (state) => state.companyCharge,
  );
  const { branchSettings } = useAppSelector((state) => state.settings);

  const {
    cartItems,
    cartSubtotal,
    saleType,
    loadingCreate: loadingCreateInvoice,
    serverStatus,
  } = useAppSelector((state) => state.invoice);

  // --- LOCAL STATE ---
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [customerSearch, setCustomerSearch] = useState<string>("");
  const debouncedCustomerSearch = useDebounce<string>(customerSearch, 500);

  const [isClearDialogOpen, setIsClearDialogOpen] = useState<boolean>(false);

  // --- INITIALIZATION ---
  useEffect(() => {
    if (open) {
      dispatch(checkCashierHasOpenShift());
      dispatch(getCurrentOrRecentShift());
    }
    if (currencies.length === 0) dispatch(getCurrencies());
    if (companyCharges.length === 0) dispatch(getCompanyCharges());
  }, [companyCharges.length, currencies.length, dispatch, open]);

  useEffect(() => {
    if (open && hasOpenShift) {
      dispatch(getBranchSettings());
      dispatch(getCustomers({ size: 20 }));
    }
  }, [dispatch, open, hasOpenShift]);

  const fetchItemsData = useCallback(() => {
    if (hasOpenShift) {
      dispatch(
        getItems({
          page: pageConfig.page,
          size: pageConfig.limit,
          search: debouncedSearch,
        }),
      );
    }
  }, [dispatch, pageConfig, debouncedSearch, hasOpenShift]);

  useEffect(() => {
    fetchItemsData();
  }, [fetchItemsData]);

  useEffect(() => {
    // Listen for customer search typing
    dispatch(getCustomers({ search: debouncedCustomerSearch, size: 20 }));
  }, [dispatch, debouncedCustomerSearch]);

  useEffect(() => {
    if (serverStatus?.operation === "create" && serverStatus?.status === 201) {
      dispatch(resetInvoiceServerStatus());
      onClose();
    }
  }, [serverStatus, dispatch, onClose]);

  // --- DERIVED OPTIONS ---
  const safeCustomers = Array.isArray(customers) ? customers : [];
  const safeCurrencies = Array.isArray(currencies) ? currencies : [];
  const safeCharges = Array.isArray(companyCharges) ? companyCharges : [];

  const customerOptions = useMemo(
    () =>
      safeCustomers.map((c) => ({
        value: c.id,
        label: `${c.name} (${c.primaryContact})`,
      })),
    [safeCustomers],
  );
  const currencyOptions = useMemo(
    () => safeCurrencies.map((c) => ({ value: c.code, label: c.code })),
    [safeCurrencies],
  );

  // --- HANDLERS ---
  const handleUpdateField = (
    productId: string,
    field: string,
    value: string | number,
  ): void => {
    if (value === "" || Number(value) >= 0) {
      dispatch(updateInvItemFields(productId, { [field]: value }));
    }
  };

  const handleDialogClose = (
    _event: unknown,
    reason?: "backdropClick" | "escapeKeyDown",
  ): void => {
    if (
      loadingCreateInvoice ||
      reason === "backdropClick" ||
      reason === "escapeKeyDown"
    ) {
      return;
    }
    onClose();
  };

  // --- COLUMNS ---
  const columns: TableColumn<ItemResponseDto>[] = useMemo(
    () => [
      {
        id: "name",
        label: "NAME",
        minWidth: 120,
        format: (_, row) => (
          <Box component="b" sx={{ fontWeight: "bold" }}>
            {row.name}
          </Box>
        ),
      },
      { id: "sku", label: "SKU", minWidth: 80 },
      { id: "brandName", label: "BRAND", minWidth: 80 },
      {
        id: "retailPrice",
        label: "RETAIL",
        minWidth: 100,
        format: (_, row) => (
          <Typography sx={{ color: "primary.main", fontWeight: "bold" }}>
            {formatCurrency(row.retailPrice)}
          </Typography>
        ),
      },
      {
        id: "wholesalePrice",
        label: "WHOLESALE",
        minWidth: 100,
        format: (_, row) => (
          <Typography sx={{ color: "secondary.main", fontWeight: "bold" }}>
            {formatCurrency(row.wholesalePrice)}
          </Typography>
        ),
      },
    ],
    [],
  );

  // --- FORMIK SETUP ---
  const baseCurrency = branchSettings?.defaultCurrencyCode || "KES";

  const initialValues: InvoiceFormValues = useMemo(
    () => ({
      customerId: "",
      dueDate: new Date(new Date().setDate(new Date().getDate() + 30))
        .toISOString()
        .split("T")[0],
      currencyCode: baseCurrency,
      exchangeRate: 1.0,
      chargeIds: [],
    }),
    [baseCurrency],
  );

  if (loadingHasOpenShift) return null;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          bgcolor: "background.paper",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Create New Invoice
        </Typography>
        <IconButton onClick={onClose} disabled={loadingCreateInvoice}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ p: 0, bgcolor: "background.default", overflow: "hidden" }}
      >
        {!hasOpenShift ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "50vh",
              p: 3,
            }}
          >
            <Alert severity="warning" sx={{ mb: 3 }}>
              An open shift is required to generate invoices.
            </Alert>
            <AppButton
              pill
              variant="contained"
              color="secondary"
              startIcon={<LockOpenIcon />}
              onClick={() => {
                onClose();
                navigateToTab("/shifts");
              }}
            >
              Go to Shifts Page
            </AppButton>
          </Box>
        ) : (
          <Box sx={{ width: "100%" }}>
            <FormAlert
              message={error}
              severity="error"
              closable
              onClose={() => dispatch(resetInvoiceServerStatus())}
            />

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              enableReinitialize={true}
              validateOnMount={true}
              onSubmit={(values) => {
                const isBaseCurrency = values.currencyCode === baseCurrency;
                const exchangeRate = isBaseCurrency
                  ? 1.0
                  : Number(values.exchangeRate) || 1.0;

                const payload: InvoiceRequestDto = {
                  shiftId: currentShift?.id || "",
                  customerId: values.customerId,
                  saleType,
                  issueDate: new Date().toISOString().split(".")[0] + "Z",
                  dueDate:
                    new Date(values.dueDate).toISOString().split(".")[0] + "Z",
                  currencyCode: values.currencyCode,
                  exchangeRate: exchangeRate,
                  chargeIds: values.chargeIds,
                  items: cartItems.map((i) => {
                    const docPrice = Number(i.price) / exchangeRate;
                    const docDiscount = Number(i.discount || 0) / exchangeRate;
                    return {
                      itemId: i.itemId,
                      qty: Number(i.qty),
                      price: Number(docPrice.toFixed(4)),
                      discount: Number(docDiscount.toFixed(4)),
                    };
                  }),
                };
                dispatch(createInvoice(payload));
              }}
            >
              {({
                values,
                isValid,
                setFieldValue,
              }: FormikProps<InvoiceFormValues>) => {
                // Derived Calculations inside render to stay reactive to form values
                const isBaseCurrency = values.currencyCode === baseCurrency;
                const activeExRate = isBaseCurrency
                  ? 1.0
                  : Number(values.exchangeRate) || 1.0;

                const { inclusiveChargesAmount, exclusiveChargesAmount } =
                  values.chargeIds.reduce(
                    (acc, id) => {
                      const charge = safeCharges.find((c) => c.id === id);
                      if (charge) {
                        const chargeAmount =
                          cartSubtotal * (Number(charge.rate || 0) / 100);
                        if (charge.chargeType === "INCLUSIVE") {
                          acc.inclusiveChargesAmount += chargeAmount;
                        } else {
                          acc.exclusiveChargesAmount += chargeAmount;
                        }
                      }
                      return acc;
                    },
                    { inclusiveChargesAmount: 0, exclusiveChargesAmount: 0 },
                  );

                const baseTotalDue = cartSubtotal + exclusiveChargesAmount;
                const displaySubtotal =
                  activeExRate > 0 ? cartSubtotal / activeExRate : cartSubtotal;
                const displayExclusiveCharges =
                  activeExRate > 0
                    ? exclusiveChargesAmount / activeExRate
                    : exclusiveChargesAmount;
                const displayInclusiveCharges =
                  activeExRate > 0
                    ? inclusiveChargesAmount / activeExRate
                    : inclusiveChargesAmount;
                const displayTotalDue =
                  activeExRate > 0 ? baseTotalDue / activeExRate : baseTotalDue;
                const displayCurrency = values.currencyCode;

                return (
                  <Form
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        height: isMobile ? "auto" : "80vh",
                      }}
                    >
                      {/* LEFT: PRODUCTS LIST */}
                      <Box
                        sx={{
                          flex: 2.5,
                          p: 2,
                          display: "flex",
                          flexDirection: "column",
                          borderRight: isMobile
                            ? "none"
                            : "1px solid rgba(255,255,255,0.1)",
                          overflow: "hidden",
                        }}
                      >
                        <TextField
                          size="small"
                          placeholder="Search products..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          sx={{ mb: 2, width: "100%", maxWidth: 300 }}
                        />
                        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                          <StandardTable
                            columns={columns}
                            data={items}
                            loading={loadingItems}
                            page={pageConfig.page}
                            count={pagination?.totalElements || 0}
                            rowsPerPage={pageConfig.limit}
                            onPageChange={(p) =>
                              setPageConfig((c) => ({ ...c, page: p }))
                            }
                            onRowsPerPageChange={(l) =>
                              setPageConfig((c) => ({
                                ...c,
                                limit: l,
                                page: 1,
                              }))
                            }
                            rowAction={(row) => (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => dispatch(addInvItem(row))}
                              >
                                <AddCircleIcon />
                              </IconButton>
                            )}
                          />
                        </Box>
                      </Box>

                      {/* RIGHT: CART & CONFIG */}
                      <Box
                        sx={{
                          flex: 1.0,
                          p: 2,
                          display: "flex",
                          flexDirection: "column",
                          bgcolor: "rgba(255,255,255,0.02)",
                          overflow: "hidden",
                          maxHeight: "100%",
                        }}
                      >
                        <Stack spacing={1.5} sx={{ flexShrink: 0, mb: 1 }}>
                          <Stack direction="row" spacing={1}>
                            <AppButton
                              fullWidth
                              pill
                              variant="contained"
                              color={
                                saleType === SaleType.RETAIL
                                  ? "primary"
                                  : "inherit"
                              }
                              onClick={() =>
                                dispatch(changeInvoiceSaleType(SaleType.RETAIL))
                              }
                              sx={{
                                opacity: saleType === SaleType.RETAIL ? 1 : 0.4,
                              }}
                            >
                              Retail
                            </AppButton>
                            <AppButton
                              fullWidth
                              pill
                              variant="contained"
                              color={
                                saleType === SaleType.WHOLESALE
                                  ? "secondary"
                                  : "inherit"
                              }
                              onClick={() =>
                                dispatch(
                                  changeInvoiceSaleType(SaleType.WHOLESALE),
                                )
                              }
                              sx={{
                                opacity:
                                  saleType === SaleType.WHOLESALE ? 1 : 0.4,
                              }}
                            >
                              Wholesale
                            </AppButton>
                          </Stack>

                          <FormSearchSelect
                            name="customerId"
                            label="Customer (Required)"
                            options={customerOptions}
                            loading={loadingCustomers}
                            onSearchChange={(text) => setCustomerSearch(text)}
                            onOptionSelect={(val) => {
                              setFieldValue("customerId", val);
                            }}
                          />
                          <Stack direction="row" spacing={1}>
                            <FormDateField
                              value={values.dueDate}
                              label="Due Date"
                              onChange={(value) =>
                                setFieldValue("dueDate", value || "")
                              }
                              required
                            />
                            <FormControl fullWidth size="small">
                              <InputLabel>Apply Charges</InputLabel>
                              <Select
                                multiple
                                name="chargeIds"
                                value={values.chargeIds}
                                onChange={(e) =>
                                  setFieldValue("chargeIds", e.target.value)
                                }
                                input={<OutlinedInput label="Apply Charges" />}
                                renderValue={(selected) => (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                    }}
                                  >
                                    {(selected as string[]).map((val) => {
                                      const charge = safeCharges.find(
                                        (c) => c.id === val,
                                      );
                                      return (
                                        <Chip
                                          key={val}
                                          label={`${charge?.name} (${charge?.chargeType === "INCLUSIVE" ? "Incl." : "Excl."})`}
                                          size="small"
                                          onDelete={() =>
                                            setFieldValue(
                                              "chargeIds",
                                              values.chargeIds.filter(
                                                (id) => id !== val,
                                              ),
                                            )
                                          }
                                          onMouseDown={(e) =>
                                            e.stopPropagation()
                                          }
                                        />
                                      );
                                    })}
                                  </Box>
                                )}
                              >
                                {safeCharges.map((charge) => (
                                  <MenuItem key={charge.id} value={charge.id}>
                                    {charge.name} ({charge.rate}%) -{" "}
                                    {charge.chargeType}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Stack>

                          <Stack direction="row" spacing={1}>
                            <FormSearchSelect
                              name="currencyCode"
                              label="Currency"
                              options={currencyOptions}
                            />
                            <FormTextField
                              name="exchangeRate"
                              label="Exchange Rate"
                              type="number"
                              disabled={isBaseCurrency}
                            />
                          </Stack>
                        </Stack>

                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold", mt: 1, mb: 1 }}
                        >
                          Invoice Items
                        </Typography>
                        <Stack
                          spacing={1}
                          sx={{ flexGrow: 1, overflowY: "auto", pr: 0.5 }}
                        >
                          {cartItems.length === 0 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              align="center"
                              sx={{ mt: 2 }}
                            >
                              Cart is empty.
                            </Typography>
                          ) : (
                            cartItems.map((cItem) => {
                              const rowBaseTotal =
                                (Number(cItem.price) -
                                  Number(cItem.discount || 0)) *
                                Number(cItem.qty);
                              const rowDisplayTotal =
                                activeExRate > 0
                                  ? rowBaseTotal / activeExRate
                                  : rowBaseTotal;

                              return (
                                <Box
                                  key={cItem.itemId}
                                  sx={{
                                    p: 1.5,
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    borderRadius: 2,
                                    bgcolor: "rgba(255,255,255,0.03)",
                                  }}
                                >
                                  <Stack direction="column" spacing={1}>
                                    <Stack
                                      direction="row"
                                      sx={{
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: "bold",
                                          pr: 1,
                                          mb: 0.5,
                                        }}
                                      >
                                        {cItem.name}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          dispatch(removeInvItem(cItem.itemId))
                                        }
                                        sx={{ mt: -0.5, mr: -0.5 }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                    <Stack
                                      direction="row"
                                      sx={{
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1.5,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "text.secondary" }}
                                        noWrap
                                      >
                                        {cItem.brand ? `${cItem.brand} | ` : ""}
                                        {cItem.category || ""}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: "bold",
                                          color: "success.main",
                                          ml: 1,
                                        }}
                                      >
                                        {formatCurrency(rowDisplayTotal)}
                                      </Typography>
                                    </Stack>

                                    <Stack
                                      direction="row"
                                      spacing={1.5}
                                      sx={{ alignItems: "center" }}
                                    >
                                      <TextField
                                        label="Price"
                                        size="small"
                                        type="number"
                                        value={cItem.price}
                                        onChange={(e) =>
                                          handleUpdateField(
                                            cItem.itemId,
                                            "price",
                                            e.target.value,
                                          )
                                        }
                                        sx={{ flex: 1, minWidth: 60 }}
                                        slotProps={{
                                          input: {
                                            style: { fontSize: "0.8rem" },
                                          },
                                        }}
                                      />
                                      <TextField
                                        label="Disc/Unit"
                                        size="small"
                                        type="number"
                                        value={
                                          cItem.discount === 0
                                            ? ""
                                            : cItem.discount
                                        }
                                        placeholder="0"
                                        onChange={(e) =>
                                          handleUpdateField(
                                            cItem.itemId,
                                            "discount",
                                            e.target.value,
                                          )
                                        }
                                        sx={{ flex: 1, minWidth: 60 }}
                                        slotProps={{
                                          input: {
                                            style: { fontSize: "0.8rem" },
                                          },
                                        }}
                                      />
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        sx={{ alignItems: "center" }}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleUpdateField(
                                              cItem.itemId,
                                              "qty",
                                              Number(cItem.qty) - 1,
                                            )
                                          }
                                        >
                                          <RemoveCircleIcon fontSize="small" />
                                        </IconButton>
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={cItem.qty}
                                          onChange={(e) =>
                                            handleUpdateField(
                                              cItem.itemId,
                                              "qty",
                                              e.target.value,
                                            )
                                          }
                                          sx={{ width: 85 }}
                                          slotProps={{
                                            input: {
                                              style: {
                                                textAlign: "center",
                                                fontSize: "0.8rem",
                                              },
                                            },
                                          }}
                                        />
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleUpdateField(
                                              cItem.itemId,
                                              "qty",
                                              Number(cItem.qty) + 1,
                                            )
                                          }
                                        >
                                          <AddCircleIcon fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                    </Stack>
                                  </Stack>
                                </Box>
                              );
                            })
                          )}
                        </Stack>

                        {/* --- TOTALS PANE --- */}
                        <Box
                          sx={{
                            flexShrink: 0,
                            mt: 2,
                            pt: 2,
                            borderTop: "1px dashed rgba(255,255,255,0.1)",
                          }}
                        >
                          <Stack
                            direction="row"
                            sx={{ justifyContent: "space-between", mb: 1 }}
                          >
                            <Typography>Cart Subtotal:</Typography>
                            <Typography>
                              {displayCurrency}{" "}
                              {formatPlainNumber(displaySubtotal)}
                            </Typography>
                          </Stack>
                          {displayExclusiveCharges > 0 && (
                            <Stack
                              direction="row"
                              sx={{ justifyContent: "space-between", mb: 1 }}
                            >
                              <Typography sx={{ color: "text.secondary" }}>
                                Exclusive Charges (+):
                              </Typography>
                              <Typography sx={{ color: "text.secondary" }}>
                                {displayCurrency}{" "}
                                {formatPlainNumber(displayExclusiveCharges)}
                              </Typography>
                            </Stack>
                          )}
                          {displayInclusiveCharges > 0 && (
                            <Stack
                              direction="row"
                              sx={{ justifyContent: "space-between", mb: 1 }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                              >
                                ↳ Includes internal charges of {displayCurrency}{" "}
                                {formatPlainNumber(displayInclusiveCharges)}
                              </Typography>
                            </Stack>
                          )}
                          <Stack
                            direction="row"
                            sx={{ justifyContent: "space-between", my: 2 }}
                          >
                            <Typography variant="h6">Invoice Total:</Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: "bold",
                                color: "secondary.main",
                              }}
                            >
                              {displayCurrency}{" "}
                              {formatPlainNumber(displayTotalDue)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <AppButton
                              pill
                              variant="outlined"
                              color="error"
                              fullWidth
                              onClick={() => setIsClearDialogOpen(true)}
                            >
                              Clear
                            </AppButton>
                            <AppButton
                              pill
                              variant="contained"
                              color="primary"
                              fullWidth
                              type="submit"
                              disabled={
                                cartItems.length === 0 ||
                                !isValid ||
                                loadingCreateInvoice
                              }
                              isLoading={loadingCreateInvoice} // Remove if your AppButton doesn't have an isLoading prop
                            >
                              {loadingCreateInvoice
                                ? "Processing..."
                                : "Generate Invoice"}
                            </AppButton>
                          </Stack>
                        </Box>
                      </Box>
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        )}
      </DialogContent>

      <ConfirmDialog
        open={isClearDialogOpen}
        onClose={() => setIsClearDialogOpen(false)}
        onConfirm={() => {
          dispatch(clearInvCart());
          setIsClearDialogOpen(false);
        }}
        title="Clear Invoice"
        description="Remove all items and reset configuration?"
        confirmText="Clear"
      />
    </Dialog>
  );
};

export default CreateInvoiceModal;
